import React, { useState } from 'react';
import { HeroSection } from '../../components/HeroSection';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, Check, X, Minus } from 'lucide-react';
export function FatFrameworks() {
  const [activeSize, setActiveSize] = useState<number | null>(null);
  const spectrum = [{
    label: 'Smallfat',
    sizes: 'US 14–18',
    color: 'bg-brand-magenta/20',
    borderColor: 'border-brand-magenta',
    textColor: 'text-brand-magenta',
    description: 'People at the lower end of the fat spectrum. Often able to shop in straight-size stores, fit in most public seating, and navigate medical care with less friction.',
    access: {
      clothing: 'high',
      seating: 'high',
      medical: 'moderate',
      employment: 'moderate',
      representation: 'moderate',
      mobility: 'high'
    },
    notes: 'Smallfats experience fatphobia but retain significant access and privilege compared to larger fat people. Important to acknowledge this privilege within fat spaces.'
  }, {
    label: 'Midfat',
    sizes: 'US 18–24',
    color: 'bg-brand-magenta/35',
    borderColor: 'border-brand-magenta',
    textColor: 'text-brand-magenta',
    description: 'People in the middle of the fat spectrum. May struggle to find clothing in mainstream stores, experience some seating limitations, and face more medical discrimination.',
    access: {
      clothing: 'moderate',
      seating: 'moderate',
      medical: 'low',
      employment: 'low',
      representation: 'low',
      mobility: 'moderate'
    },
    notes: 'Midfats often straddle the line between "acceptable" and "unacceptable" fatness. They may be included in corporate body positivity but excluded from true fat liberation resources.'
  }, {
    label: 'Superfat',
    sizes: 'US 24–32',
    color: 'bg-brand-magenta/55',
    borderColor: 'border-brand-magenta',
    textColor: 'text-brand-magenta',
    description: 'People at the higher end of the fat spectrum. Face significant barriers in clothing, public spaces, healthcare, and employment. Often invisible in media representation.',
    access: {
      clothing: 'low',
      seating: 'low',
      medical: 'very-low',
      employment: 'very-low',
      representation: 'very-low',
      mobility: 'low'
    },
    notes: 'Superfats face compounded discrimination. Medical providers may refuse care, employers may not hire, and public spaces are often physically inaccessible.'
  }, {
    label: 'Infinifat',
    sizes: 'US 32+',
    color: 'bg-brand-magenta/75',
    borderColor: 'border-brand-magenta',
    textColor: 'text-white',
    description: 'People at the largest end of the fat spectrum. Face the most extreme barriers across all areas of life. Almost completely erased from media, fashion, and public discourse.',
    access: {
      clothing: 'very-low',
      seating: 'very-low',
      medical: 'very-low',
      employment: 'very-low',
      representation: 'very-low',
      mobility: 'very-low'
    },
    notes: "Infinifats are the most marginalized within the fat community. Fat liberation must center their experiences and access needs, or it's not liberation—it's just slightly expanded acceptability."
  }];
  const accessAreas = [{
    key: 'clothing',
    label: 'Clothing Access',
    description: 'Ability to find clothing in stores and online'
  }, {
    key: 'seating',
    label: 'Public Seating',
    description: 'Fitting in chairs, booths, transit, theaters'
  }, {
    key: 'medical',
    label: 'Medical Care',
    description: 'Receiving dignified, weight-neutral healthcare'
  }, {
    key: 'employment',
    label: 'Employment',
    description: 'Hiring, workplace treatment, advancement'
  }, {
    key: 'representation',
    label: 'Media Representation',
    description: 'Seeing yourself in media, fashion, culture'
  }, {
    key: 'mobility',
    label: 'Physical Mobility',
    description: 'Moving through public spaces without barriers'
  }];
  const getAccessIcon = (level: string) => {
    switch (level) {
      case 'high':
        return <Check className="h-5 w-5 text-brand-lime" data-id="element-1111" />;
      case 'moderate':
        return <Minus className="h-5 w-5 text-brand-orange" data-id="element-1112" />;
      case 'low':
        return <X className="h-5 w-5 text-brand-magenta" data-id="element-1113" />;
      case 'very-low':
        return <X className="h-5 w-5 text-red-600" data-id="element-1114" />;
      default:
        return null;
    }
  };
  const getAccessLabel = (level: string) => {
    switch (level) {
      case 'high':
        return 'Generally accessible';
      case 'moderate':
        return 'Sometimes accessible';
      case 'low':
        return 'Rarely accessible';
      case 'very-low':
        return 'Almost never accessible';
      default:
        return '';
    }
  };
  return <div className="min-h-screen bg-brand-cream" data-id="element-1115">
      <HeroSection title="Fat Frameworks" subtitle="Understanding the fat spectrum, levels of access, and why size matters within fat liberation." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-1116" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1117">
        <div className="prose prose-lg" data-id="element-1118">
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1119">
            Not all fat people experience fatphobia the same way. The{' '}
            <strong className="text-brand-magenta" data-id="element-1120">fat spectrum</strong> is a
            framework for understanding how size affects access, privilege, and
            oppression within the fat community. The larger you are, the more
            barriers you face.
          </p>
          <p className="text-lg text-gray-600 mt-4" data-id="element-1121">
            These categories were developed by fat activists to name the
            differences in lived experience that size creates—and to ensure fat
            liberation centers those who are most marginalized.
          </p>
        </div>
      </section>

      {/* The Spectrum - Visual */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1122">
        <h2 className="text-3xl font-black uppercase mb-4 text-brand-black" data-id="element-1123">
          The Fat Spectrum
        </h2>
        <p className="text-gray-600 mb-12" data-id="element-1124">
          Click any category to learn more about access and barriers at that
          size.
        </p>

        {/* Spectrum Bar */}
        <div className="mb-12" data-id="element-1125">
          <div className="flex rounded-sm overflow-hidden border-2 border-brand-black" data-id="element-1126">
            {spectrum.map((size, index) => <button key={index} onClick={() => setActiveSize(activeSize === index ? null : index)} className={`flex-1 py-6 px-4 ${size.color} transition-all relative ${activeSize === index ? 'ring-4 ring-brand-magenta ring-inset' : ''}`} data-id="element-1127">
                <div className={`text-center ${index >= 2 ? 'text-white' : 'text-brand-black'}`} data-id="element-1128">
                  <p className="text-lg md:text-xl font-black uppercase" data-id="element-1129">
                    {size.label}
                  </p>
                  <p className="text-xs md:text-sm opacity-75 mt-1" data-id="element-1130">
                    {size.sizes}
                  </p>
                </div>
              </button>)}
          </div>

          {/* Arrow indicating increasing oppression */}
          <div className="flex items-center justify-between mt-4 px-2" data-id="element-1131">
            <span className="text-xs font-bold uppercase text-gray-500" data-id="element-1132">
              More access
            </span>
            <div className="flex-1 mx-4 h-px bg-gradient-to-r from-brand-lime via-brand-orange to-brand-magenta" data-id="element-1133"></div>
            <span className="text-xs font-bold uppercase text-gray-500" data-id="element-1134">
              More barriers
            </span>
          </div>
        </div>

        {/* Expanded Detail */}
        {activeSize !== null && <motion.div key={activeSize} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white border-2 border-brand-black p-8 mb-12" data-id="element-1135">
            <div className="flex flex-col lg:flex-row gap-8" data-id="element-1136">
              <div className="lg:w-1/3" data-id="element-1137">
                <div className={`inline-block px-4 py-2 ${spectrum[activeSize].color} border-2 ${spectrum[activeSize].borderColor} mb-4`} data-id="element-1138">
                  <span className={`text-2xl font-black uppercase ${activeSize >= 2 ? 'text-white' : 'text-brand-black'}`} data-id="element-1139">
                    {spectrum[activeSize].label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-4" data-id="element-1140">
                  {spectrum[activeSize].sizes}
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-1141">
                  {spectrum[activeSize].description}
                </p>
              </div>

              <div className="lg:w-2/3" data-id="element-1142">
                <h4 className="text-sm font-bold uppercase text-gray-500 mb-4" data-id="element-1143">
                  Access Levels:
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1144">
                  {accessAreas.map(area => {
                const level = spectrum[activeSize].access[area.key as keyof (typeof spectrum)[0]['access']];
                return <div key={area.key} className="flex items-start gap-3 p-3 bg-gray-50" data-id="element-1145">
                        {getAccessIcon(level)}
                        <div data-id="element-1146">
                          <p className="text-sm font-bold" data-id="element-1147">{area.label}</p>
                          <p className="text-xs text-gray-500" data-id="element-1148">
                            {getAccessLabel(level)}
                          </p>
                        </div>
                      </div>;
              })}
                </div>

                <div className="mt-6 p-4 bg-brand-magenta/5 border-l-4 border-brand-magenta" data-id="element-1149">
                  <p className="text-sm text-gray-700 italic" data-id="element-1150">
                    {spectrum[activeSize].notes}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>}
      </section>

      {/* Access Comparison Table */}
      <section className="py-16 bg-white" data-id="element-1151">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1152">
          <h2 className="text-3xl font-black uppercase mb-8 text-brand-black" data-id="element-1153">
            Access at a Glance
          </h2>

          <div className="overflow-x-auto" data-id="element-1154">
            <table className="w-full border-2 border-brand-black" data-id="element-1155">
              <thead data-id="element-1156">
                <tr className="bg-brand-magenta text-white" data-id="element-1157">
                  <th className="p-4 text-left font-black uppercase text-sm border-r-2 border-brand-black" data-id="element-1158">
                    Access Area
                  </th>
                  {spectrum.map((size, i) => <th key={i} className="p-4 text-center font-black uppercase text-sm border-r-2 border-brand-black last:border-r-0" data-id="element-1159">
                      {size.label}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1160">
                {accessAreas.map((area, i) => <tr key={area.key} className={`border-t-2 border-brand-black ${i % 2 === 1 ? 'bg-gray-50' : ''}`} data-id="element-1161">
                    <td className="p-4 font-bold text-sm border-r-2 border-brand-black" data-id="element-1162">
                      {area.label}
                      <p className="font-normal text-xs text-gray-500 mt-1" data-id="element-1163">
                        {area.description}
                      </p>
                    </td>
                    {spectrum.map((size, j) => {
                  const level = size.access[area.key as keyof typeof size.access];
                  return <td key={j} className="p-4 text-center border-r-2 border-brand-black last:border-r-0" data-id="element-1164">
                          <div className="flex justify-center" data-id="element-1165">
                            {getAccessIcon(level)}
                          </div>
                        </td>;
                })}
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-6 mt-6 text-xs" data-id="element-1166">
            <div className="flex items-center gap-2" data-id="element-1167">
              <Check className="h-4 w-4 text-brand-lime" data-id="element-1168" />
              <span data-id="element-1169">Generally accessible</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1170">
              <Minus className="h-4 w-4 text-brand-orange" data-id="element-1171" />
              <span data-id="element-1172">Sometimes accessible</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1173">
              <X className="h-4 w-4 text-brand-magenta" data-id="element-1174" />
              <span data-id="element-1175">Rarely accessible</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1176">
              <X className="h-4 w-4 text-red-600" data-id="element-1177" />
              <span data-id="element-1178">Almost never accessible</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1179">
        <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-1180">
          Why This Framework Matters
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1181">
          <div className="bg-white border-2 border-brand-black p-8" data-id="element-1182">
            <h3 className="text-xl font-black uppercase mb-4 text-brand-magenta" data-id="element-1183">
              Within Fat Spaces
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1184">
              Without naming size differences, fat spaces default to centering
              smallfat experiences. Smallfats may dominate conversations,
              leadership, and representation while superfats and infinifats
              remain invisible.
            </p>
            <ul className="space-y-2 text-sm text-gray-700" data-id="element-1185">
              <li className="flex items-start" data-id="element-1186">
                <span className="text-brand-magenta mr-2" data-id="element-1187">→</span>Conference
                speakers are disproportionately smallfat
              </li>
              <li className="flex items-start" data-id="element-1188">
                <span className="text-brand-magenta mr-2" data-id="element-1189">→</span>"Fat fashion"
                rarely goes above size 28
              </li>
              <li className="flex items-start" data-id="element-1190">
                <span className="text-brand-magenta mr-2" data-id="element-1191">→</span>Body positive
                campaigns feature mostly smallfats
              </li>
              <li className="flex items-start" data-id="element-1192">
                <span className="text-brand-magenta mr-2" data-id="element-1193">→</span>Access needs
                of larger fat people are afterthoughts
              </li>
            </ul>
          </div>

          <div className="bg-white border-2 border-brand-black p-8" data-id="element-1194">
            <h3 className="text-xl font-black uppercase mb-4 text-brand-magenta" data-id="element-1195">
              Within Broader Society
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1196">
              Understanding the spectrum helps name how anti-fatness escalates
              with size. It's not a binary of "fat or not fat"—it's a gradient
              of increasing exclusion, violence, and erasure.
            </p>
            <ul className="space-y-2 text-sm text-gray-700" data-id="element-1197">
              <li className="flex items-start" data-id="element-1198">
                <span className="text-brand-magenta mr-2" data-id="element-1199">→</span>Medical denial
                increases with size
              </li>
              <li className="flex items-start" data-id="element-1200">
                <span className="text-brand-magenta mr-2" data-id="element-1201">→</span>Employment
                discrimination worsens with size
              </li>
              <li className="flex items-start" data-id="element-1202">
                <span className="text-brand-magenta mr-2" data-id="element-1203">→</span>Physical
                spaces become inaccessible at larger sizes
              </li>
              <li className="flex items-start" data-id="element-1204">
                <span className="text-brand-magenta mr-2" data-id="element-1205">→</span>Social stigma
                and dehumanization intensify
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Intersections */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-1206">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1207">
          <h2 className="text-3xl font-black uppercase mb-8" data-id="element-1208">
            Size + Other Identities
          </h2>
          <p className="text-lg text-gray-200 mb-12" data-id="element-1209">
            The fat spectrum doesn't exist in isolation. Size intersects with
            race, disability, gender, class, and other identities to create
            unique experiences of oppression.
          </p>

          <div className="space-y-6" data-id="element-1210">
            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1211">
              <h3 className="font-bold text-lg text-brand-magenta mb-2" data-id="element-1212">
                Size + Race
              </h3>
              <p className="text-gray-200 text-sm" data-id="element-1213">
                Fat Black women face both anti-Blackness and anti-fatness. At
                larger sizes, this compounds: more medical violence, more
                employment discrimination, more dehumanization. The "mammy"
                stereotype specifically targets fat Black women.
              </p>
            </div>

            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1214">
              <h3 className="font-bold text-lg text-brand-magenta mb-2" data-id="element-1215">
                Size + Disability
              </h3>
              <p className="text-gray-200 text-sm" data-id="element-1216">
                Fat disabled people—especially at larger sizes—face compounded
                barriers. Mobility aids have weight limits. Medical providers
                attribute all symptoms to weight. Accessibility features rarely
                account for size.
              </p>
            </div>

            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1217">
              <h3 className="font-bold text-lg text-brand-magenta mb-2" data-id="element-1218">
                Size + Gender
              </h3>
              <p className="text-gray-200 text-sm" data-id="element-1219">
                Fat women and femmes face gendered fatphobia—their worth is tied
                to desirability. Fat trans people face barriers to
                gender-affirming care that increase with size. Fat men
                experience fatphobia differently but are not exempt.
              </p>
            </div>

            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1220">
              <h3 className="font-bold text-lg text-brand-magenta mb-2" data-id="element-1221">
                Size + Class
              </h3>
              <p className="text-gray-200 text-sm" data-id="element-1222">
                Poor fat people can't buy their way out of inaccessibility.
                Plus-size clothing is expensive. Bariatric equipment costs more.
                The "solutions" to fatphobia are often only available to those
                with money.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1223">
        <div className="bg-white border-2 border-brand-black p-8" data-id="element-1224">
          <div className="flex items-start gap-4" data-id="element-1225">
            <AlertTriangle className="h-8 w-8 text-brand-magenta flex-shrink-0 mt-1" data-id="element-1226" />
            <div data-id="element-1227">
              <h3 className="text-xl font-black uppercase mb-4 text-brand-black" data-id="element-1228">
                A Note on Using This Framework
              </h3>
              <div className="space-y-3 text-gray-700" data-id="element-1229">
                <p data-id="element-1230">
                  These categories are{' '}
                  <strong data-id="element-1231">tools for analysis, not rigid boxes</strong>. People's
                  sizes change. Bodies are complex. The goal isn't to police who
                  belongs where—it's to name the reality that size affects
                  access and to center those with the least.
                </p>
                <p data-id="element-1232">
                  <strong data-id="element-1233">This framework should never be used to:</strong>
                </p>
                <ul className="space-y-2 ml-4" data-id="element-1234">
                  <li className="flex items-start" data-id="element-1235">
                    <span className="text-brand-magenta mr-2" data-id="element-1236">✗</span>Gate-keep
                    who is "fat enough" for fat spaces
                  </li>
                  <li className="flex items-start" data-id="element-1237">
                    <span className="text-brand-magenta mr-2" data-id="element-1238">✗</span>Shame
                    people for their size category
                  </li>
                  <li className="flex items-start" data-id="element-1239">
                    <span className="text-brand-magenta mr-2" data-id="element-1240">✗</span>Create
                    hierarchies of suffering
                  </li>
                  <li className="flex items-start" data-id="element-1241">
                    <span className="text-brand-magenta mr-2" data-id="element-1242">✗</span>Dismiss
                    smallfat experiences of fatphobia
                  </li>
                </ul>
                <p data-id="element-1243">
                  <strong data-id="element-1244">It should be used to:</strong>
                </p>
                <ul className="space-y-2 ml-4" data-id="element-1245">
                  <li className="flex items-start" data-id="element-1246">
                    <span className="text-brand-lime mr-2" data-id="element-1247">✓</span>Center the
                    most marginalized in fat spaces
                  </li>
                  <li className="flex items-start" data-id="element-1248">
                    <span className="text-brand-lime mr-2" data-id="element-1249">✓</span>Acknowledge
                    privilege within fatness
                  </li>
                  <li className="flex items-start" data-id="element-1250">
                    <span className="text-brand-lime mr-2" data-id="element-1251">✓</span>Design spaces
                    and resources for the largest bodies
                  </li>
                  <li className="flex items-start" data-id="element-1252">
                    <span className="text-brand-lime mr-2" data-id="element-1253">✓</span>Understand
                    how oppression scales with size
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}